import {Signal, WritableSignal, computed, signal} from '@angular/core';
import {BehaviorSubject, Observable, distinctUntilChanged, map} from 'rxjs';

export type EntityId = string | number;

/**
 * Root shape of a signal entity store. Mirrors Akita's EntityState, including
 * the index signature that lets session stores keep extra root properties
 * (session_id, flow_id, ...) alongside the entity collection.
 */
export interface EntityState<E = any> {
  entities?: Record<EntityId, E>;
  ids?: EntityId[];
  active?: EntityId | null;
  loading?: boolean;
  [key: string]: any;
}

export interface SignalStoreConfig {
  /** Former Akita store name — also used to seed from the old persistState blob. */
  name: string;
  idKey?: string;
  /** Extra root properties for the initial state (session stores). */
  initialState?: Record<string, any>;
  /**
   * Where this store persists. Defaults to 'session': entity stores hold
   * in-progress flow/view state (a quote, a claim, a policy being edited)
   * that must stay isolated to the tab it's running in — sharing it via
   * localStorage lets two tabs silently clobber each other's flow. Opt into
   * 'local' only for state that should genuinely persist and be shared
   * across every open tab.
   */
  scope?: 'session' | 'local';
}

const STORAGE_PREFIX = 'state:';

/**
 * Signal-backed replacement for the subset of Akita's EntityStore this app
 * uses: set / setActive / update(root partial) / reset / getValue.
 * State lives in a signal; state$ is an Akita-compatible synchronous RxJS
 * bridge that powers SignalEntityQuery's Observables — prefer the signals
 * in new code.
 *
 * Persists on every commit (parity with Akita persistState) to sessionStorage
 * by default — see SignalStoreConfig.scope — seeding once from the old
 * 'AkitaStores' localStorage blob so live sessions survive.
 */
export class SignalEntityStore<E = any, S extends EntityState<E> = EntityState<E>> {
  readonly name: string;
  private readonly idKey: string;
  private readonly initialState: S;
  private readonly scope: 'session' | 'local';

  private readonly _state: WritableSignal<S>;
  readonly state$: BehaviorSubject<S>;

  readonly stateSignal: Signal<S>;
  readonly entitiesList: Signal<E[]>;
  readonly activeEntity: Signal<E | undefined>;

  constructor(config: SignalStoreConfig) {
    this.name = config.name;
    this.idKey = config.idKey ?? 'id';
    this.scope = config.scope ?? 'session';
    this.initialState = {
      entities: {},
      ids: [],
      active: null,
      // Akita entity stores start loading:true; set() flips it false.
      loading: true,
      ...(config.initialState ?? {})
    } as S;
    this._state = signal<S>(this.loadPersisted());
    this.state$ = new BehaviorSubject<S>(this._state());
    this.stateSignal = this._state.asReadonly();
    this.entitiesList = computed(() => {
      const s = this._state();
      return (s.ids ?? []).map(id => s.entities?.[id]).filter(e => e !== undefined) as E[];
    });
    this.activeEntity = computed(() => {
      const s = this._state();
      return s.active != null ? s.entities?.[s.active] : undefined;
    });
  }

  getValue(): S {
    return this._state();
  }

  /**
   * Replaces the whole entity collection; root properties are untouched.
   * Optional second arg sets the active id (raw id or Akita-style {activeId}).
   */
  set(entities: E[], activeIdOrOptions?: EntityId | {activeId?: EntityId}): void {
    const current = this._state();
    const ids: EntityId[] = [];
    const map: Record<EntityId, E> = {};
    for (const entity of entities ?? []) {
      const id = (entity as any)?.[this.idKey];
      ids.push(id);
      map[id] = entity;
    }
    let active = current.active != null && map[current.active] !== undefined ? current.active : null;
    if (activeIdOrOptions !== undefined) {
      const requested = typeof activeIdOrOptions === 'object' ? activeIdOrOptions?.activeId : activeIdOrOptions;
      if (requested != null && map[requested] !== undefined) {
        active = requested;
      }
    }
    this.commit({...current, entities: map, ids, active, loading: false});
  }

  setLoading(loading: boolean): void {
    this.commit({...this._state(), loading});
  }

  setActive(id: EntityId | null): void {
    this.commit({...this._state(), active: id ?? null});
  }

  /** Merges a partial into the ACTIVE entity (no-op when none is active). */
  updateActive(entityOrCallback: Partial<E> | ((entity: E) => Partial<E>)): void {
    const active = this._state().active;
    if (active == null) {
      return;
    }
    this.update(active, entityOrCallback);
  }

  /**
   * update(partial | fn)      → merges into the ROOT state.
   * update(id, partial | fn)  → merges into ONE entity (Akita's entity update).
   */
  update(stateOrCallback: Partial<S> | ((state: S) => Partial<S>)): void;
  update(id: EntityId, entityOrCallback: Partial<E> | ((entity: E) => Partial<E>)): void;
  update(idOrState: any, entityOrCallback?: Partial<E> | ((entity: E) => Partial<E>)): void {
    const current = this._state();
    if (entityOrCallback !== undefined) {
      const id: EntityId = idOrState;
      const entity = current.entities?.[id];
      if (entity === undefined) {
        return;
      }
      const partial = typeof entityOrCallback === 'function' ? entityOrCallback(entity) : entityOrCallback;
      this.commit({
        ...current,
        entities: {...current.entities, [id]: {...entity, ...partial}}
      });
      return;
    }
    const partial = typeof idOrState === 'function' ? idOrState(current) : idOrState;
    this.commit({...current, ...partial});
  }

  reset(): void {
    this.commit({...this.initialState});
  }

  private get backingStorage(): Storage {
    return this.scope === 'local' ? localStorage : sessionStorage;
  }

  private commit(next: S): void {
    this._state.set(next);
    this.state$.next(next);
    try {
      this.backingStorage.setItem(STORAGE_PREFIX + this.name, JSON.stringify(next));
    } catch {
    }
  }

  private loadPersisted(): S {
    try {
      const own = this.backingStorage.getItem(STORAGE_PREFIX + this.name);
      if (own) {
        return {...this.initialState, ...JSON.parse(own)};
      }
      // Legacy blob predates the session/local split — always localStorage.
      const akita = localStorage.getItem('AkitaStores');
      if (akita) {
        const slice = JSON.parse(akita)?.[this.name];
        if (slice) {
          return {...this.initialState, ...slice};
        }
      }
    } catch {
    }
    return {...this.initialState};
  }
}

export interface SelectAllOptions<E> {
  filterBy?: ((entity: E) => boolean) | Array<(entity: E) => boolean>;
  limitTo?: number;
}

/**
 * Signal-backed replacement for the used subset of Akita's QueryEntity.
 * Observables emit synchronously on subscribe (Akita parity) via the store's
 * BehaviorSubject bridge.
 */
export class SignalEntityQuery<E = any, S extends EntityState<E> = EntityState<E>> {
  constructor(protected store: SignalEntityStore<E, S>) {
  }

  getValue(): S {
    return this.store.getValue();
  }

  getAll(): E[] {
    return this.store.entitiesList();
  }

  getEntity(id: EntityId): E | undefined {
    return this.store.getValue().entities?.[id];
  }

  hasEntity(id: EntityId): boolean {
    return this.store.getValue().entities?.[id] !== undefined;
  }

  // Returns any, matching Akita's QueryEntity typing (IDType defaulted to any) —
  // call sites assign this to number/string fields.
  getActiveId(): any {
    return this.store.getValue().active;
  }

  getActive(): E | undefined {
    return this.store.activeEntity();
  }

  /** select() → whole state; select(key) → that root property, distinct. */
  select(): Observable<S>;
  select(key: keyof S | string): Observable<any>;
  select(key?: keyof S | string): Observable<any> {
    if (key === undefined) {
      return this.store.state$.asObservable();
    }
    return this.store.state$.pipe(
      map(state => state[key as string]),
      distinctUntilChanged()
    );
  }

  selectAll(options?: SelectAllOptions<E>): Observable<E[]> {
    return this.store.state$.pipe(
      distinctUntilChanged((a, b) => a.entities === b.entities && a.ids === b.ids),
      map(state => {
        let list = (state.ids ?? []).map(id => state.entities?.[id]).filter(e => e !== undefined) as E[];
        const filterBy = options?.filterBy;
        if (filterBy) {
          const predicates = Array.isArray(filterBy) ? filterBy : [filterBy];
          list = list.filter(entity => predicates.every(predicate => predicate(entity)));
        }
        if (options?.limitTo !== undefined) {
          list = list.slice(0, options.limitTo);
        }
        return list;
      })
    );
  }

  /**
   * selectEntity(id)              → the entity.
   * selectEntity(predicate)       → first entity matching the predicate.
   * selectEntity(id, 'key' | fn)  → a projected property of the entity (Akita).
   */
  selectEntity(id: EntityId): Observable<E | undefined>;
  selectEntity(predicate: (entity: E) => boolean): Observable<E | undefined>;
  selectEntity<K extends keyof E>(id: EntityId, project: K): Observable<E[K] | undefined>;
  selectEntity<R>(id: EntityId, project: (entity: E) => R): Observable<R | undefined>;
  selectEntity(idOrPredicate: any, project?: any): Observable<any> {
    return this.store.state$.pipe(
      map(state => {
        let entity: E | undefined;
        if (typeof idOrPredicate === 'function') {
          for (const id of state.ids ?? []) {
            const candidate = state.entities?.[id];
            if (candidate !== undefined && idOrPredicate(candidate)) {
              entity = candidate;
              break;
            }
          }
        } else {
          entity = state.entities?.[idOrPredicate];
        }
        if (project === undefined || entity === undefined) {
          return entity;
        }
        return typeof project === 'function' ? project(entity) : (entity as any)[project];
      }),
      distinctUntilChanged()
    );
  }

  selectActive(): Observable<E | undefined>;
  selectActive<R>(project: (entity: E) => R): Observable<R | undefined>;
  selectActive(project?: (entity: E) => any): Observable<any> {
    return this.store.state$.pipe(
      map(state => {
        const entity = state.active != null ? state.entities?.[state.active] : undefined;
        if (project === undefined || entity === undefined) {
          return entity;
        }
        return project(entity);
      }),
      distinctUntilChanged()
    );
  }

  selectFirst(): Observable<E | undefined> {
    return this.store.state$.pipe(
      map(state => {
        const firstId = state.ids?.[0];
        return firstId !== undefined ? state.entities?.[firstId] : undefined;
      }),
      distinctUntilChanged()
    );
  }

  selectLoading(): Observable<boolean> {
    return this.store.state$.pipe(
      map(state => !!state.loading),
      distinctUntilChanged()
    );
  }
}
